import { getSupabase as getSupabaseNullable } from "../../lib/supabase";
import { fromDb } from "./baseService";

function requireSupabase() {
  const client = getSupabaseNullable();
  if (!client) throw new Error("Supabase not initialized. Call initSupabase() first.");
  return client;
}

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface DeviceInfo {
  device_type: "mobile" | "desktop" | "tablet";
  client_type: "web_browser" | "native_app";
  device_name: string;
  device_fingerprint: string;
}

export interface ActiveSession {
  sessionId: string;
  deviceType: string;
  clientType: string;
  deviceName: string | null;
  ipAddress: string | null;
  lastHeartbeat: string;
  createdAt: string;
}

export interface SessionAccessResult {
  allowed: boolean;
  reason?: "OPERATOR_MOBILE_ONLY";
  /** Name of the device that was auto-kicked (for toast notification) */
  kickedDeviceName?: string | null;
}

export interface CreateSessionResult {
  sessionId: string;
  success: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Session limits by license type
// ─────────────────────────────────────────────────────────────────────────────

const SESSION_LIMITS: Record<string, number> = {
  operator: 1,
  full_user: 2,
};

/** Sessions with heartbeats older than this are considered ghosts */
const GHOST_THRESHOLD_SECONDS = 90;

// ─────────────────────────────────────────────────────────────────────────────
// Service
// ─────────────────────────────────────────────────────────────────────────────

export class SessionService {
  /**
   * Check whether a user is allowed to create a new session.
   * V2: system admins bypass all checks; ghost sessions cleaned first; auto-kick oldest.
   */
  static async checkSessionAccess(
    userId: string,
    licenseType: string,
    deviceInfo: DeviceInfo,
    isSystemAdmin = false,
  ): Promise<SessionAccessResult> {
    // Rule 1: System admins bypass all session checks
    if (isSystemAdmin) {
      return { allowed: true };
    }

    // Operator license: web browser / desktop not allowed
    if (licenseType === "operator" && deviceInfo.client_type === "web_browser") {
      return { allowed: false, reason: "OPERATOR_MOBILE_ONLY" };
    }
    if (licenseType === "operator" && deviceInfo.device_type === "desktop") {
      return { allowed: false, reason: "OPERATOR_MOBILE_ONLY" };
    }

    // Rule 3: Clean up ghost sessions first
    await SessionService.cleanupGhostSessions(userId);

    // Count only fresh sessions
    const activeSessions = await SessionService.getFreshActiveSessions(userId);
    const limit = SESSION_LIMITS[licenseType] ?? 1;

    // Rule 2: Auto-kick oldest if at limit
    if (activeSessions.length >= limit) {
      const oldest = activeSessions[0]; // sorted by created_at ASC
      const kicked = await SessionService.forceCloseSession(oldest.sessionId, userId);
      if (kicked) {
        return { allowed: true, kickedDeviceName: oldest.deviceName };
      }
    }

    return { allowed: true };
  }

  /**
   * Create a new active session row.
   */
  static async createSession(
    userId: string,
    tenantId: string,
    licenseType: string,
    deviceInfo: DeviceInfo,
  ): Promise<CreateSessionResult> {
    try {
      const supabase = requireSupabase();

      const { data, error } = await supabase
        .from("active_sessions")
        .insert({
          user_id: userId,
          tenant_id: tenantId,
          license_type: licenseType,
          device_type: deviceInfo.device_type,
          client_type: deviceInfo.client_type,
          device_fingerprint: deviceInfo.device_fingerprint,
          device_name: deviceInfo.device_name,
        })
        .select("id")
        .single();

      if (error) {
        return { sessionId: "", success: false };
      }

      return { sessionId: data.id, success: true };
    } catch {
      return { sessionId: "", success: false };
    }
  }

  /**
   * Force-close a session (used for auto-kick).
   */
  static async forceCloseSession(sessionIdToClose: string, userId: string): Promise<boolean> {
    try {
      const supabase = requireSupabase();

      const { data, error } = await supabase.rpc("terminate_session", {
        p_session_id: sessionIdToClose,
        p_user_id: userId,
        p_reason: "kicked",
      });

      if (error) return false;
      return data === true;
    } catch {
      return false;
    }
  }

  /**
   * Get only fresh active sessions (heartbeat within threshold).
   * Sorted by created_at ASC (oldest first) for auto-kick logic.
   */
  static async getFreshActiveSessions(userId: string): Promise<ActiveSession[]> {
    try {
      const supabase = requireSupabase();

      const { data, error } = await supabase.rpc("get_fresh_active_sessions", {
        p_user_id: userId,
        p_max_age_seconds: GHOST_THRESHOLD_SECONDS,
      });

      if (error || !data) return [];

      return (data as Record<string, unknown>[]).map((row) =>
        fromDb<ActiveSession>(row),
      );
    } catch {
      return [];
    }
  }

  /**
   * Terminate ghost sessions for a user (heartbeat older than threshold).
   */
  static async cleanupGhostSessions(userId: string): Promise<number> {
    try {
      const supabase = requireSupabase();

      const { data, error } = await supabase.rpc("cleanup_ghost_sessions", {
        p_user_id: userId,
        p_max_age_seconds: GHOST_THRESHOLD_SECONDS,
      });

      if (error) return 0;
      return (data as number) ?? 0;
    } catch {
      return 0;
    }
  }

  /**
   * Send a heartbeat to keep the session alive.
   */
  static async sendHeartbeat(sessionId: string): Promise<boolean> {
    try {
      const supabase = requireSupabase();

      const { error } = await supabase
        .from("active_sessions")
        .update({ last_heartbeat: new Date().toISOString() })
        .eq("id", sessionId)
        .eq("status", "active");

      return !error;
    } catch {
      return false;
    }
  }

  /**
   * Check if a session is still active (not timed out or terminated).
   */
  static async isSessionValid(sessionId: string): Promise<boolean> {
    try {
      const supabase = requireSupabase();

      const { data, error } = await supabase
        .from("active_sessions")
        .select("status")
        .eq("id", sessionId)
        .single();

      if (error || !data) return false;
      return data.status === "active";
    } catch {
      return false;
    }
  }

  /**
   * Get all active sessions for a user via RPC (legacy — kept for backward compat).
   */
  static async getActiveSessions(userId: string): Promise<ActiveSession[]> {
    try {
      const supabase = requireSupabase();

      const { data, error } = await supabase.rpc("get_active_sessions", {
        p_user_id: userId,
      });

      if (error || !data) return [];

      return (data as Record<string, unknown>[]).map((row) =>
        fromDb<ActiveSession>(row),
      );
    } catch {
      return [];
    }
  }

  /**
   * Terminate the current session (e.g. on user logout).
   */
  static async terminateCurrentSession(sessionId: string, userId: string): Promise<void> {
    try {
      const supabase = requireSupabase();

      await supabase.rpc("terminate_session", {
        p_session_id: sessionId,
        p_user_id: userId,
        p_reason: "user_logout",
      });
    } catch {
      // Best-effort — session will be cleaned up by pg_cron if this fails
    }
  }
}
